import React, { useContext, useEffect, useRef, useState } from 'react';
import { Bell, Menu, PanelLeft, Plus } from 'lucide-react';
import { Menu as HeadlessMenu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import {
  AcademicCapIcon,
  ArrowRightOnRectangleIcon as SignOutIcon,
  ComputerDesktopIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { UserContext } from '../../UserContext';
import { supabase } from '../../supabaseClient';
import { resetWorkspaceState, setWorkspaceLoaded } from '../../store/workspacesSlice';
import { persistor } from '../../store';
import { captureConnectionsEvent } from '../../lib/captureConnectionsEvent';
import { getWorkspaceDetailPath } from '../../lib/resolveMostRecentWorkspace';
import {
  deleteWorkspace,
  duplicateWorkspace,
  importPublicWorkspace,
  getWorkspaceCounts,
} from '../../lib/workspaceActions';
import useSidebarWorkspaces from '../../hooks/useSidebarWorkspaces';
import usePublicWorkspaces from '../../hooks/usePublicWorkspaces';
import { useIsSmartphone } from '../../hooks/useIsSmartphone';
import { usePremium } from '../Subscription/PremiumProvider';
import OnboardingTour from '../OnboardingTour/OnboardingTour';
import DeleteWorkspaceModal from '../DeleteWorkspaceModal';
import DuplicateWorkspaceModal from '../DuplicateWorkspaceModal';
import OfflineStatusBanner from '../OfflineStatusBanner/OfflineStatusBanner';
import LearnSidebar from './LearnSidebar';
import ExamCountdown from './ExamCountdown';
import FeedbackField from '../FeedbackField';

const LEARN_SIDEBAR_COLLAPSED_KEY = 'learn_sidebar_collapsed';

const readSidebarCollapsed = () => {
  try {
    return localStorage.getItem(LEARN_SIDEBAR_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
};

/**
 * Shared shell for Connections: sidebar + top bar with quick-add + account menu.
 */
const LearnLayout = ({
  workspaceId,
  workspaceName,
  masteryFilter,
  onMasteryFilterChange,
  onNewWorkspace,
  onQuickAdd,
  feedbackView = '',
  onBasketsMasteryClick,
  onUnfiledRemainingClick,
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useContext(UserContext);
  const {
    isPremium,
    isLoading: isPremiumLoading,
    getSubscriptionText,
    openUpgradeModal,
    userProfile: premiumProfile,
  } = usePremium();
  const isSmartphone = useIsSmartphone();

  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isDesktopSidebarCollapsed, setIsDesktopSidebarCollapsed] = useState(readSidebarCollapsed);
  const desktopSidebarRef = useRef(null);
  const [showOnboardingTour, setShowOnboardingTour] = useState(false);
  const [mobilePopupDismissedInDb, setMobilePopupDismissedInDb] = useState(null);
  const [showMobilePopup, setShowMobilePopup] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [workspaceToDelete, setWorkspaceToDelete] = useState(null);
  const [isDeletingWorkspace, setIsDeletingWorkspace] = useState(false);
  const [deleteCounts, setDeleteCounts] = useState(null);

  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [workspaceToDuplicate, setWorkspaceToDuplicate] = useState(null);
  const [includeBaskets, setIncludeBaskets] = useState(true);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [duplicateProgress, setDuplicateProgress] = useState(0);
  const [duplicateStatus, setDuplicateStatus] = useState('');

  const [importingWorkspaceId, setImportingWorkspaceId] = useState(null);

  const { workspaces, renameInList, removeFromList, addToList } = useSidebarWorkspaces(
    user?.id,
    workspaceId,
    location.state?.workspaceName
  );

  const { publicWorkspaces, importedOriginalIds, markImported, refreshImported } =
    usePublicWorkspaces(user?.id);

  const resolvedWorkspaceName =
    workspaces.find((w) => w.id === workspaceId)?.name ??
    location.state?.workspaceName ??
    workspaceName ??
    'Workspace';

  useEffect(() => {
    let cancelled = false;

    const fetchMobilePopupPreference = async () => {
      if (!isSmartphone || !user?.id) {
        setMobilePopupDismissedInDb(null);
        return;
      }

      try {
        const { data: profile, error } = await supabase
          .from('user_profiles')
          .select('preferences')
          .eq('id', user.id)
          .single();

        if (error) throw error;
        if (!cancelled) {
          setMobilePopupDismissedInDb(!!profile?.preferences?.mobile_popup_dismissed);
        }
      } catch (error) {
        console.error('Error fetching mobile popup preference:', error);
        if (!cancelled) setMobilePopupDismissedInDb(false);
      }
    };

    fetchMobilePopupPreference();
    return () => {
      cancelled = true;
    };
  }, [user?.id, isSmartphone]);

  useEffect(() => {
    if (
      isSmartphone &&
      mobilePopupDismissedInDb === false &&
      !showOnboardingTour
    ) {
      setShowMobilePopup(true);
    }
  }, [isSmartphone, mobilePopupDismissedInDb, showOnboardingTour]);

  const handleDismissMobilePopup = async () => {
    setShowMobilePopup(false);

    if (!user?.id) return;

    try {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('preferences')
        .eq('id', user.id)
        .single();

      const preferences = {
        ...(profile?.preferences || {}),
        mobile_popup_dismissed: true,
      };

      await supabase
        .from('user_profiles')
        .update({
          preferences,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      setMobilePopupDismissedInDb(true);
    } catch (error) {
      console.error('Error saving mobile popup preference:', error);
    }
  };

  const handleOpenMobileSidebar = () => {
    setIsMobileSidebarOpen(true);
  };

  const handleCloseMobileSidebar = () => {
    setIsMobileSidebarOpen(false);
  };

  const handleToggleDesktopSidebar = () => {
    setIsDesktopSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(LEARN_SIDEBAR_COLLAPSED_KEY, next ? 'true' : 'false');
      } catch {
        // Preference is still applied for this session.
      }
      return next;
    });
  };

  useEffect(() => {
    const sidebar = desktopSidebarRef.current;
    if (!sidebar) return;
    if (isDesktopSidebarCollapsed) {
      sidebar.setAttribute('inert', '');
    } else {
      sidebar.removeAttribute('inert');
    }
  }, [isDesktopSidebarCollapsed]);

  const handleQuickAdd = () => {
    captureConnectionsEvent('quick_add_opened', { source: 'fab' });
    onQuickAdd?.();
  };

  const handleRequestDuplicate = (workspace) => {
    if (!isPremiumLoading && !isPremium()) {
      openUpgradeModal('duplicate');
      return;
    }
    setWorkspaceToDuplicate(workspace);
    setIncludeBaskets(true);
    setShowDuplicateModal(true);
  };

  const handleCloseDuplicateModal = () => {
    if (isDuplicating) return;
    setShowDuplicateModal(false);
    setWorkspaceToDuplicate(null);
    setIncludeBaskets(true);
    setDuplicateProgress(0);
    setDuplicateStatus('');
  };

  const handleConfirmDuplicate = async () => {
    if (!workspaceToDuplicate || isDuplicating || !user?.id) return;

    setIsDuplicating(true);
    setDuplicateProgress(0);
    setDuplicateStatus('Creating new workspace...');

    try {
      const newWorkspace = await duplicateWorkspace({
        workspace: workspaceToDuplicate,
        userId: user.id,
        includeBaskets,
        onProgress: (percent, statusText) => {
          setDuplicateProgress(percent);
          setDuplicateStatus(statusText);
        },
      });

      addToList({
        id: newWorkspace.id,
        name: newWorkspace.name,
        created_at: newWorkspace.created_at || new Date().toISOString(),
      });

      captureConnectionsEvent('workspace_duplicated', {
        workspace_id: newWorkspace.id,
        source_workspace_id: workspaceToDuplicate.id,
        include_baskets: includeBaskets,
        source: 'sidebar',
      });

      dispatch(setWorkspaceLoaded(false));
      handleCloseMobileSidebar();
      navigate(getWorkspaceDetailPath(newWorkspace), {
        state: {
          workspaceName: newWorkspace.name,
          workspaceOpenSource: 'created',
        },
      });

      setTimeout(() => {
        setShowDuplicateModal(false);
        setWorkspaceToDuplicate(null);
        setIncludeBaskets(true);
        setDuplicateProgress(0);
        setDuplicateStatus('');
      }, 500);
    } catch (error) {
      console.error('Error duplicating workspace:', error);
      setDuplicateStatus(`Error: ${error.message}`);
      toast.error('Failed to duplicate workspace. Please try again.');
    } finally {
      setIsDuplicating(false);
    }
  };

  const handleRequestDelete = async (workspace) => {
    setWorkspaceToDelete(workspace);
    setIsDeletingWorkspace(false);
    setDeleteCounts(null);
    setShowDeleteModal(true);
    try {
      const counts = await getWorkspaceCounts(workspace.id);
      setDeleteCounts(counts);
    } catch (error) {
      console.error('Error loading delete counts:', error);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setIsDeletingWorkspace(false);
    setWorkspaceToDelete(null);
    setDeleteCounts(null);
  };

  const handleConfirmDelete = async () => {
    if (!isDeletingWorkspace) {
      setIsDeletingWorkspace(true);
      return;
    }

    if (!workspaceToDelete) return;

    const deletedId = workspaceToDelete.id;
    const wasOpen = deletedId === workspaceId;
    const remaining = workspaces.filter((w) => w.id !== deletedId);
    const fallback = remaining[0] || null;

    try {
      await deleteWorkspace({ workspaceId: deletedId });

      removeFromList(deletedId);
      await refreshImported();
      captureConnectionsEvent('workspace_deleted', {
        workspace_id: deletedId,
        basket_count: deleteCounts?.basketCount ?? 0,
        topic_count: deleteCounts?.topicCount ?? 0,
        was_open: wasOpen,
        source: 'sidebar',
      });

      handleCancelDelete();
      handleCloseMobileSidebar();

      if (wasOpen) {
        dispatch(setWorkspaceLoaded(false));
        if (fallback) {
          navigate(getWorkspaceDetailPath(fallback), {
            state: {
              workspaceName: fallback.name,
              workspaceOpenSource: 'manual_switch',
            },
          });
        } else {
          navigate('/workspace', {
            replace: true,
            state: { reason: 'last_deleted' },
          });
        }
      }
    } catch (error) {
      console.error('Error deleting workspace:', error);
      toast.error('Failed to delete workspace. Please try again.');
    }
  };

  const handleImportPublic = async (workspace) => {
    if (!user?.id || importingWorkspaceId) return;

    setImportingWorkspaceId(workspace.id);
    try {
      const { newWorkspace } = await importPublicWorkspace({
        workspace,
        userId: user.id,
        source: 'sidebar',
      });

      addToList({
        id: newWorkspace.id,
        name: newWorkspace.name,
        created_at: newWorkspace.created_at || new Date().toISOString(),
      });
      markImported(workspace.id);

      toast.success('Successfully imported.');

      dispatch(setWorkspaceLoaded(false));
      handleCloseMobileSidebar();
      navigate(getWorkspaceDetailPath(newWorkspace), {
        state: {
          workspaceName: newWorkspace.name,
          workspaceOpenSource: 'created',
        },
      });
    } catch (error) {
      console.error('Error importing workspace:', error);
      toast.error('Failed to import workspace. Please try again.');
    } finally {
      setImportingWorkspaceId(null);
    }
  };

  const handleLogout = async () => {
    captureConnectionsEvent('account_menu_item_tapped', {
      item: 'sign_out',
      source: 'sidebar',
    });
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      dispatch(resetWorkspaceState());
      await persistor.flush();
      await persistor.purge();
      window.location.assign('/');
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Could not sign out. Please try again.');
    }
  };

  const handleOpenTour = () => {
    captureConnectionsEvent('account_menu_item_tapped', {
      item: 'tour',
      source: 'sidebar',
    });
    setShowOnboardingTour(true);
  };

  const handleOpenUpgrade = () => {
    captureConnectionsEvent('account_menu_item_tapped', {
      item: 'upgrade',
      source: 'sidebar',
    });
    openUpgradeModal('account_menu');
  };

  const emailInitial = (user?.email || '?').charAt(0).toUpperCase();
  const fallbackWorkspaceName =
    workspaceToDelete?.id === workspaceId && workspaces.length > 1
      ? workspaces.find((w) => w.id !== workspaceId)?.name
      : undefined;
  const willCreateEmptyWorkspace =
    workspaceToDelete?.id === workspaceId && workspaces.length === 1;

  const sidebarSharedProps = {
    workspaceId,
    workspaceName: resolvedWorkspaceName,
    masteryFilter,
    onMasteryFilterChange,
    onNewWorkspace,
    onBasketsMasteryClick,
    onUnfiledRemainingClick,
    workspaces,
    renameInList,
    publicWorkspaces,
    importedOriginalIds,
    onRequestDuplicate: handleRequestDuplicate,
    onRequestDelete: handleRequestDelete,
    onImportPublic: handleImportPublic,
    importingWorkspaceId,
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex">
      {/* Desktop sidebar */}
      <div
        id="learn-sidebar"
        ref={desktopSidebarRef}
        className={`hidden md:block md:fixed md:inset-y-0 md:left-0 z-30 overflow-hidden transition-all duration-200 ease-out ${
          isDesktopSidebarCollapsed ? 'md:w-0' : 'md:w-64'
        }`}
        aria-hidden={isDesktopSidebarCollapsed}
      >
        <div className="flex h-full w-64 flex-col">
          <LearnSidebar
            {...sidebarSharedProps}
            onCollapse={handleToggleDesktopSidebar}
          />
        </div>
      </div>

      {/* Mobile sidebar overlay */}
      {isMobileSidebarOpen && (
        <div className="md:hidden fixed inset-0 z-40 flex">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close sidebar overlay"
            onClick={handleCloseMobileSidebar}
          />
          <div className="relative w-72 max-w-[85vw] h-full z-10 shadow-xl">
            <LearnSidebar
              {...sidebarSharedProps}
              onCloseMobile={handleCloseMobileSidebar}
            />
          </div>
        </div>
      )}

      <div
        className={`flex-1 flex flex-col min-w-0 transition-all duration-200 ease-out ${
          isDesktopSidebarCollapsed ? 'md:pl-0' : 'md:pl-64'
        }`}
      >
        <header className="bg-white sticky top-0 z-20 border-b border-neutral-200">
          <div className="px-3 sm:px-6 h-14 sm:h-16 flex items-center gap-2 sm:gap-3 min-w-0">
            <button
              type="button"
              onClick={handleOpenMobileSidebar}
              className="md:hidden p-2 rounded-lg text-neutral-600 hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-cranberry-700"
              aria-label="Open sidebar"
            >
              <Menu size={20} aria-hidden="true" />
            </button>

            {isDesktopSidebarCollapsed && (
              <button
                type="button"
                onClick={handleToggleDesktopSidebar}
                className="hidden md:inline-flex p-2 rounded-lg text-neutral-600 hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-cranberry-700"
                aria-label="Open sidebar"
                aria-expanded="false"
                aria-controls="learn-sidebar"
              >
                <PanelLeft size={20} aria-hidden="true" />
              </button>
            )}

            <div className="flex items-center gap-3 flex-1 min-w-0 overflow-hidden">
              <h1 className="text-base sm:text-lg font-semibold text-neutral-900 truncate min-w-0">
                {resolvedWorkspaceName}
              </h1>
              <ExamCountdown workspaceId={workspaceId} />
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
              <FeedbackField view={feedbackView} />

              <button
                type="button"
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-full text-neutral-500 hover:bg-neutral-100 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-cranberry-700"
                aria-label="Notifications"
              >
                <Bell size={20} aria-hidden="true" />
              </button>

              <HeadlessMenu as="div" className="relative">
                <MenuButton
                  className="relative w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-neutral-200 text-neutral-800 text-sm font-semibold flex items-center justify-center hover:bg-neutral-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-cranberry-700"
                  aria-label="Account menu"
                  aria-haspopup="menu"
                >
                  {emailInitial}
                  {isPremium() && (
                    <span
                      className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-butter-500 border-2 border-white"
                      aria-hidden="true"
                    />
                  )}
                </MenuButton>
                <Transition
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <MenuItems className="absolute right-0 mt-2 w-64 origin-top-right bg-white rounded-xl border-2 border-neutral-900 shadow-lg z-50 focus:outline-none">
                    <div className="px-4 py-4 border-b-2 border-neutral-200">
                      <p className="text-sm font-medium text-neutral-900 mb-1">Profile</p>
                      <p className="text-xs text-neutral-600 truncate mb-3">
                        {user?.email}
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          {isPremium() ? (
                            <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-white text-xs font-medium border-2 border-neutral-900 bg-emerald-500">
                              Premium Plan
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="px-4 py-1.5 rounded-lg bg-cranberry-700 text-white text-xs font-medium border-2 border-neutral-900 hover:bg-cranberry-600 transition-colors"
                              onClick={handleOpenUpgrade}
                            >
                              Upgrade to Premium
                            </button>
                          )}
                        </div>
                        <div className="text-xs text-neutral-600">
                          <p>{isPremium() ? getSubscriptionText() : 'Free plan'}</p>
                          {isPremium() && (
                            <p className="mt-1">
                              Plan ends:{' '}
                              {premiumProfile?.subscription_end_date
                                ? new Date(
                                    premiumProfile.subscription_end_date
                                  ).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                  })
                                : 'N/A'}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="py-2">
                      <MenuItem>
                        {({ focus }) => (
                          <button
                            type="button"
                            onClick={handleOpenTour}
                            className={`${
                              focus ? 'bg-neutral-100' : ''
                            } flex items-center w-full px-4 py-3 text-sm text-neutral-900`}
                          >
                            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 mr-3">
                              <AcademicCapIcon className="h-5 w-5 text-amber-800" />
                            </div>
                            <div className="flex flex-col items-start">
                              <span className="font-medium">How to use Basket</span>
                              <span className="text-xs text-neutral-500">Take a quick tour</span>
                            </div>
                          </button>
                        )}
                      </MenuItem>
                      <MenuItem>
                        {({ focus }) => (
                          <button
                            type="button"
                            onClick={handleLogout}
                            className={`${
                              focus ? 'bg-neutral-100' : ''
                            } flex items-center w-full px-4 py-2.5 text-sm text-cranberry-700`}
                          >
                            <SignOutIcon className="h-4 w-4 mr-3 text-cranberry-700" />
                            Sign out
                          </button>
                        )}
                      </MenuItem>
                    </div>
                  </MenuItems>
                </Transition>
              </HeadlessMenu>
            </div>
          </div>
        </header>

        <OfflineStatusBanner />

        <main className="flex-1 flex flex-col min-h-0 pb-24">{children}</main>
      </div>

      <DeleteWorkspaceModal
        isOpen={showDeleteModal}
        workspace={workspaceToDelete}
        isConfirmStep={isDeletingWorkspace}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        fallbackWorkspaceName={fallbackWorkspaceName}
        willCreateEmptyWorkspace={willCreateEmptyWorkspace}
      />

      <DuplicateWorkspaceModal
        isOpen={showDuplicateModal}
        workspace={workspaceToDuplicate}
        includeBaskets={includeBaskets}
        onIncludeBasketsChange={setIncludeBaskets}
        isDuplicating={isDuplicating}
        duplicateProgress={duplicateProgress}
        duplicateStatus={duplicateStatus}
        onCancel={handleCloseDuplicateModal}
        onConfirm={handleConfirmDuplicate}
      />

      {user && (
        <OnboardingTour
          isOpen={showOnboardingTour}
          onClose={() => setShowOnboardingTour(false)}
          userId={user.id}
        />
      )}

      {showMobilePopup && !showOnboardingTour && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mobile-popup-title"
          aria-describedby="mobile-popup-description"
        >
          <div className="bg-white rounded-xl border-2 border-neutral-900 shadow-lg max-w-sm w-full mx-4">
            <div className="px-6 py-4 border-b-2 border-neutral-200 flex justify-between items-start">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <ComputerDesktopIcon className="h-6 w-6 text-amber-800" aria-hidden />
                </div>
                <h2 id="mobile-popup-title" className="text-lg font-semibold text-neutral-900">
                  Best on Bigger Screens
                </h2>
              </div>
              <button
                type="button"
                onClick={handleDismissMobilePopup}
                className="p-1.5 hover:bg-neutral-100 rounded-lg transition-colors"
                aria-label="Dismiss"
              >
                <XMarkIcon className="h-5 w-5 text-neutral-600" aria-hidden />
              </button>
            </div>
            <div className="px-6 py-4">
              <p id="mobile-popup-description" className="text-neutral-600">
                For the best experience, open Basket on a device with a larger screen. Organizing and reviewing topics on a small screen can get cramped fast.
              </p>
            </div>
            <div className="px-6 py-4 bg-neutral-50 rounded-b-xl border-t-2 border-neutral-200">
              <button
                type="button"
                onClick={handleDismissMobilePopup}
                className="w-full px-4 py-2.5 bg-cranberry-700 text-white rounded-xl border-2 border-neutral-900 font-medium hover:bg-cranberry-600 transition-colors"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={handleQuickAdd}
        aria-label="Add"
        className="fixed z-40 bottom-4 right-4 flex h-[52px] w-[52px] items-center justify-center rounded-full border-2 border-black bg-[#9f1239] text-white shadow-[4px_4px_0_#171717] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9f1239] focus-visible:ring-offset-2 md:bottom-6 md:right-6 md:h-[60px] md:w-[60px]"
      >
        <Plus size={26} aria-hidden="true" strokeWidth={2.5} />
      </button>
    </div>
  );
};

export default LearnLayout;
